import { useEffect, useMemo, useState } from 'react'
import { SmartBack } from '../navigation/SmartBack.js'
import { Eye, EyeOff, ExternalLink, MapPin, MoreHorizontal, Plus } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link, navigate } from '../router.js'
import { Drawer, Select, adminMutate, usePermissions, useToast } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { jourFr, heureFr } from './lib.js'
import { MarketEventCard } from './MarketEventCard.js'
import {
  PAST_RANGES, PERIODES, TRIS, defaultPeriode, trier,
  type PastRange, type Periode, type Tri,
} from './market-nav.js'
import { MarketEventDrawer } from './MarketEventDrawer.js'

/**
 * /soirees/market — VEILLE CONCURRENTIELLE (v4).
 *
 * Des SIGNAUX canoniques (promo, guest, sold out…) et des ÉVÉNEMENTS
 * concurrents — jamais des certitudes : chaque carte porte source(s),
 * confiance, dernière vérification. Hiérarchie visuelle par IMPACT :
 * une entrée gratuite concurrente saute aux yeux, un simple événement non.
 * Chaque élément cliquable ouvre un VRAI détail (règle : pas de clic mort).
 */

/**
 * Une soirée concurrente, telle que l'API la rend.
 *
 * DEUX AXES, jamais confondus : `relevance_level` dit l'IMPACT sur nous,
 * `confidence` dit la FIABILITÉ de l'information.
 */
interface MarketEvent {
  id: string; title: string; starts_at: string; ends_at: string | null
  venue_id: string | null; venue_name: string | null
  city: string | null; address: string | null; artists: string[]
  category: string | null
  distance_km: number | null; relevance: number
  relevance_level: 'FORT' | 'MOYEN' | 'FAIBLE'
  relevance_factors: { label: string; points: number }[]
  confidence: string; status: string
  sources: { name: string; url: string; seen_at: string }[]
  last_verified_at: string; first_seen_at: string
  capture_content_id: string | null
  hidden: boolean
  business_night_date: string | null
  announced_date: string | null
  announced_time: string | null
  /** Groupe temporel calculé par le domaine — jamais recalculé ici. */
  bucket: Periode
  past_range: PastRange | null
}

interface Signal {
  id: string; venue_id: string | null; venue_name: string | null
  market_event_id: string | null; market_event_title: string | null
  type: string; type_label: string; title: string; summary: string
  audience: string | null; condition_time: string | null
  event_start_at: string | null; first_seen_at: string; last_seen_at: string
  sources: {
    name: string; url: string | null; type: string; seen_at: string
    provenance?: {
      platform: string; content_type: string; external_content_id: string
      author_handle: string | null; published_at: string | null
    }
  }[]
  confidence: 'HIGH' | 'MEDIUM' | 'LOW'; impact: 'HIGH' | 'MEDIUM' | 'LOW'
  status: string
  history: { at: string; summary: string; condition_time: string | null }[]
  /** Écarté des vues métier — jamais supprimé, toujours réversible. */
  hidden: boolean
  origin: 'AUTO' | 'MANUAL'
  venue_archived: boolean
  /* Le signal porte DÉSORMAIS son propre groupe temporel. Il le déduisait de
     l'événement le plus proche dans le temps : une seconde règle, côté écran,
     qui n'avait aucune raison de rester d'accord avec celle du domaine. */
  bucket: Periode
  past_range: PastRange | null
  /** Preuves enrichies — l'API rapproche l'URL de ce que la recherche web
   *  sait déjà de cette page. Absente sur les données anciennes. */
  evidence?: Evidence[]
}

interface Evidence {
  url: string | null; domain: string | null; path: string | null
  label: string; kind: string; seen_at: string
  page_title: string | null; heading: string | null; snippet: string | null
  query: string | null; published_at: string | null; engine: string | null
  first_seen_at: string | null; last_seen_at: string | null
  times_seen: number | null; fetch_error: string | null
}

/** Le type de source, dit en français — « social_reference » ne parle à personne. */
function sourceKindFr(src: { type: string; provenance?: { platform: string; content_type: string } }): string {
  if (src.type === 'MANUAL') return 'saisie manuelle'
  if (src.provenance) {
    const p = src.provenance.platform === 'INSTAGRAM' ? 'Instagram' : src.provenance.platform
    const t = src.provenance.content_type === 'STORY' ? 'story'
      : src.provenance.content_type === 'REEL' ? 'reel' : 'publication'
    return `${p} · ${t}`
  }
  const FR: Record<string, string> = {
    WEBSITE: 'site officiel', EVENT_PAGE: 'page agenda', PUBLIC_TICKETING: 'billetterie',
    PUBLIC_AGENDA: 'agenda public', RSS: 'flux RSS', ICS: 'calendrier',
    SEARCH_ENGINE: 'recherche web', SOCIAL_REFERENCE: 'réseau social',
  }
  return FR[src.type] ?? src.type.toLowerCase()
}

interface VenueRow {
  id: string; name: string; enabled: boolean; status: string
  category: string; priority_level: string
  distance_km?: string | number | null
}

/** Les deux axes du produit, ramenés à l'échelle du tri (§66 : la confiance
 *  n'est PAS un multiplicateur d'impact — ce sont deux dimensions). */
const NIVEAU_IMPACT: Record<string, 'HIGH' | 'MEDIUM' | 'LOW'> =
  { FORT: 'HIGH', MOYEN: 'MEDIUM', FAIBLE: 'LOW' }
const NIVEAU_CONFIANCE: Record<string, 'HIGH' | 'MEDIUM' | 'LOW'> =
  { CONFIRMED: 'HIGH', LIKELY: 'MEDIUM', HIGH: 'HIGH', MEDIUM: 'MEDIUM', LOW: 'LOW' }

/** Distance de l'établissement d'un signal — `null` si on ne la connaît pas.
 *  Une distance inconnue n'est pas zéro : elle passe en fin de tri. */
function distancePour(venueId: string | null, venues: VenueRow[] | null): number | null {
  if (!venueId) return null
  const v = (venues ?? []).find((x) => x.id === venueId)
  const d = v?.distance_km
  return d === null || d === undefined ? null : Number(d)
}

const IMPACT_FR: Record<string, string> = { HIGH: 'Fort impact', MEDIUM: 'Impact moyen', LOW: 'Impact faible' }
const CONF_FR: Record<string, string> = { HIGH: 'élevée', MEDIUM: 'moyenne', LOW: 'faible' }
const STATUS_FR: Record<string, string> = { ACTIVE: 'actif', UNKNOWN: 'à confirmer', EXPIRED: 'expiré', CANCELLED: 'annulé' }

const IMPACT_OPTIONS = [
  { value: 'all', label: 'Tout impact' },
  { value: 'HIGH', label: 'Fort impact' },
  { value: 'MEDIUM', label: 'Moyen et plus' },
]
const STATUS_OPTIONS = [
  { value: 'current', label: 'Actifs et à confirmer' },
  { value: 'all', label: 'Tout (expirés inclus)' },
]

const SIGNAL_TYPE_CHOICES = [
  { value: 'all', label: 'Tous les signaux' },
  { value: 'FREE', label: 'Entrées gratuites' },
  { value: 'GUEST', label: 'Guests / têtes d’affiche' },
  { value: 'STUDENT', label: 'Étudiant / BDE' },
  { value: 'PROMO', label: 'Promos / offres' },
  { value: 'CAPACITY', label: 'Sold out / places' },
  { value: 'SCHEDULE', label: 'Programmation' },
]
const TYPE_GROUPS: Record<string, string[]> = {
  FREE: ['FREE_ENTRY', 'FREE_ENTRY_CONDITIONAL', 'FREE_ENTRY_GENDER', 'FREE_ENTRY_BEFORE_TIME'],
  GUEST: ['HEADLINER', 'SPECIAL_GUEST'],
  STUDENT: ['STUDENT_EVENT', 'BDE_EVENT'],
  PROMO: ['DISCOUNT', 'PROMO_CODE', 'GUESTLIST', 'OPEN_BAR', 'FREE_DRINK', 'GIVEAWAY'],
  CAPACITY: ['SOLD_OUT', 'LOW_TICKET_WARNING'],
  SCHEDULE: ['OPENING_CHANGE', 'CLOSING_CHANGE', 'CANCELLATION', 'SCHEDULE_CHANGE'],
}

const MANUAL_TYPES = [
  { value: 'FREE_ENTRY', label: 'Entrée gratuite' },
  { value: 'FREE_ENTRY_GENDER', label: 'Entrée gratuite ciblée' },
  { value: 'SPECIAL_GUEST', label: 'Guest DJ' },
  { value: 'STUDENT_EVENT', label: 'Soirée étudiante' },
  { value: 'BDE_EVENT', label: 'Soirée BDE' },
  { value: 'OPEN_BAR', label: 'Open bar' },
  { value: 'PROMO_CODE', label: 'Code promo' },
  { value: 'SOLD_OUT', label: 'Sold out' },
  { value: 'CANCELLATION', label: 'Annulation' },
  { value: 'OTHER_COMMERCIAL_SIGNAL', label: 'Autre signal' },
]

async function fetchJson<T>(url: string): Promise<T | null> {
  try { const r = await fetch(url, { credentials: 'same-origin' }); return r.ok ? (await r.json()) as T : null }
  catch { return null }
}

/**
 * Groupes temporels — calculés PAR LE SERVEUR, en nuits commerciales.
 *
 * L'ancienne règle locale disait « tout vendredi ou samedi à moins de neuf
 * jours ». Un jeudi, elle rangeait deux week-ends différents sous le même
 * titre, et un vendredi à dix jours pouvait s'y glisser. Le classement vient
 * désormais du domaine : un seul calcul, partagé par tous les écrans.
 */
/**
 * Le signal est-il DÉJÀ dit par l'événement ?
 *
 * On ne compare pas des textes : on compare des CONCEPTS. Un signal
 * « tête d'affiche » est contenu dans un événement qui nomme cet artiste ;
 * un signal d'événement est contenu dans un événement de même titre.
 *
 * Une promotion (gratuité, open bar) n'est JAMAIS absorbée : c'est une
 * information commerciale distincte, que l'événement ne porte pas.
 */
function signalContenuDans(sig: Signal, e: MarketEvent): boolean {
  const norm = (t: string) => t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  // Les promotions restent visibles : elles ajoutent une information.
  if (/FREE|OPEN_BAR|GUESTLIST|PROMO|STUDENT|DRINK/.test(sig.type)) return false
  const artistes = (e.artists ?? []).map(norm)
  const titre = norm(e.title)
  const resume = norm(sig.summary)
  if (artistes.some((a) => a.length > 2 && resume.includes(a))) return true
  if (titre.length > 3 && resume.includes(titre)) return true
  // « DJ invité annoncé » face à un événement qui liste déjà ses invités.
  if (sig.type === 'HEADLINER' && artistes.length > 0) return true
  return false
}

/**
 * UN SIGNAL N'EST PAS UN ÉVÉNEMENT (§49).
 *
 * « AFRO PARTY le 28 » est une soirée : elle a une date, un lieu, une carte
 * `MarketEventCard`. « Entrée gratuite pour les filles avant 1 h » est une
 * information commerciale : elle influence sans être un événement. Les
 * confondre visuellement empêcherait de lire la veille.
 *
 * Ici aussi les DEUX AXES sont distincts : `impact` colore la carte,
 * `confidence` se dit en toutes lettres.
 */
function SignalCard({ s, onOpen, onMasquer, onIgnorer }: {
  s: Signal; onOpen: () => void
  onMasquer?: (() => void) | undefined
  onIgnorer?: (() => void) | undefined
}) {
  const [menu, setMenu] = useState(false)
  return (
    <article className={`so-sig so-sig--${s.impact.toLowerCase()}${s.hidden ? ' so-sig--hidden' : ''}`}>
      <button type="button" className="so-sig__open so-clickable" onClick={onOpen}
        aria-label={`Détail : ${s.venue_name ?? 'établissement à identifier'} — ${s.type_label}`}>
        <span className="so-sig__badge">{s.type_label}</span>
        <span className="so-sig__body">
          {/* §79-§80 — un établissement archivé garde son nom grâce au
              snapshot ; « à identifier » ne concerne qu'une source vraiment
              non rattachée, et se dit sans jugement. */}
          <span className="so-sig__venue">
            {s.venue_name ?? s.market_event_title ?? 'Établissement à identifier'}
          </span>
          <span className="so-sig__summary">{s.summary}</span>
          <span className="lc-hint so-sig__meta">
            {s.event_start_at ? `${jourFr(s.event_start_at)} · ` : 'Date à confirmer · '}
            {CONF_FR[s.confidence]}
            {s.status !== 'ACTIVE' ? ` · ${STATUS_FR[s.status] ?? s.status}` : ''}
            {' · vu il y a '}{ageFr(s.last_seen_at)}
          </span>
        </span>
      </button>
      {onMasquer || onIgnorer ? (
        <div className="mev__menu">
          <button type="button" className="mev__menu__btn" aria-haspopup="menu" aria-expanded={menu}
            aria-label="Actions sur ce signal" onClick={() => setMenu((o) => !o)}>
            <MoreHorizontal size={16} aria-hidden="true" />
          </button>
          {menu ? (
            <>
              <div className="mev__menu__scrim" onClick={() => setMenu(false)} aria-hidden="true" />
              <div className="mev__menu__pop lc-floating" role="menu" onClick={() => setMenu(false)}>
                {onMasquer ? (
                  <button type="button" onClick={onMasquer}>
                    {s.hidden ? 'Réafficher ce signal' : 'Masquer ce signal'}
                  </button>
                ) : null}
                {onIgnorer && !s.hidden ? (
                  <button type="button" onClick={onIgnorer}>
                    Ignorer « {s.type_label} » pour cet établissement
                  </button>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      ) : null}
      {s.hidden ? <span className="mev__hiddenTag">Masqué</span> : null}
    </article>
  )
}

function ageFr(iso: string): string {
  const mn = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000))
  if (mn < 60) return `${mn} min`
  if (mn < 48 * 60) return `${Math.round(mn / 60)} h`
  return `${Math.round(mn / 1440)} j`
}

export function MarketPage() {
  const meta = useShellMeta()
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('soiree.manage')
  const toast = useToast()
  const [data, setData] = useState<{ events: MarketEvent[]; signals: Signal[]; venues: VenueRow[] } | null>(null)
  const [impact, setImpact] = useState('all')
  const [typeGroup, setTypeGroup] = useState('all')
  const [venueFilter, setVenueFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('current')
  const [selSignal, setSelSignal] = useState<Signal | null>(null)
  const [selEvent, setSelEvent] = useState<MarketEvent | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [capture, setCapture] = useState<string | null>(null)
  // §30 — les éléments masqués restent consultables, sans encombrer la vue.
  const [voirMasques, setVoirMasques] = useState(false)
  /* Période affichée. `null` = automatique : la première qui a de la matière.
     Choisir explicitement fige le choix pour la session. */
  const [periode, setPeriode] = useState<Periode | null>(null)
  const [passeRange, setPasseRange] = useState<PastRange>('CETTE_SEMAINE')
  const [tri, setTri] = useState<Tri>('DATE')
  const [add, setAdd] = useState({ venue_id: '', type: 'FREE_ENTRY', text: '', event_day: '', source_label: '' })

  /** Masquer n'efface pas : la collecte continue, l'affichage s'arrête. */
  const masquerSignal = async (id: string, hidden: boolean) => {
    const r = await adminMutate('POST', `/api/v1/soirees/market/signals/${id}/hidden`, { hidden })
    if (r.status === 200) {
      toast('ok', hidden ? 'Signal masqué — il reste consultable.' : 'Signal réaffiché.')
      await charger()
    } else toast('error', 'Action impossible.')
  }
  const masquerEvent = async (id: string, hidden: boolean) => {
    const r = await adminMutate('POST', `/api/v1/soirees/market/events/${id}/hidden`, { hidden })
    if (r.status === 200) {
      toast('ok', hidden ? 'Soirée masquée — elle reste consultable.' : 'Soirée réaffichée.')
      await charger()
    } else toast('error', 'Action impossible.')
  }
  /** La règle porte sur le TYPE, pas sur ce signal-ci : elle vaut pour la suite. */
  const ignorerType = async (sig: Signal) => {
    if (!sig.venue_id) return
    const r = await adminMutate<{ error?: string; hiddenNow?: number }>(
      'POST', `/api/v1/soirees/market/venues/${sig.venue_id}/ignore-rules`,
      { signal_type: sig.type })
    if (r.status === 200) {
      const n = r.data?.hiddenNow ?? 0
      toast('ok', `« ${sig.type_label} » ne sera plus affiché pour ${sig.venue_name ?? 'cet établissement'}`
        + (n > 1 ? ` — ${n} informations masquées.` : '.'))
      await charger()
    } else toast('error', 'Action impossible.')
  }

  const charger = async () => {
    const status = statusFilter === 'all' ? '&status=all' : ''
    // §30 — la vue des masqués est un MODE, pas un empilement : on demande
    // les uns OU les autres, jamais un mélange qui rendrait la liste illisible.
    const masques = voirMasques ? '&hidden=only' : ''
    const [m, sg] = await Promise.all([
      fetchJson<{ events: MarketEvent[]; venues: VenueRow[] }>(
        `/api/v1/soirees/market${voirMasques ? '?hidden=only' : ''}`),
      fetchJson<{ signals: Signal[] }>(`/api/v1/soirees/market/signals?x=1${status}${masques}`),
    ])
    if (m) setData({ events: m.events, venues: m.venues as VenueRow[], signals: sg?.signals ?? [] })
  }
  useEffect(() => { void charger() }, [statusFilter, voirMasques])

  const venueOptions = useMemo(() => ([
    { value: 'all', label: 'Tous les lieux' },
    ...(data?.venues ?? []).map((v) => ({ value: v.id, label: v.name })),
  ]), [data])

  const signals = useMemo(() => (data?.signals ?? [])
    .filter((s) => impact === 'all' || (impact === 'HIGH' ? s.impact === 'HIGH' : s.impact !== 'LOW'))
    .filter((s) => typeGroup === 'all' || (TYPE_GROUPS[typeGroup] ?? []).includes(s.type))
    .filter((s) => venueFilter === 'all' || s.venue_id === venueFilter), [data, impact, typeGroup, venueFilter])

  const events = useMemo(() => (data?.events ?? [])
    .filter((e) => venueFilter === 'all' || (data?.venues.find((v) => v.id === venueFilter)?.name === e.venue_name))
    .filter((e) => impact !== 'HIGH' || e.relevance_level === 'FORT'), [data, venueFilter, impact])

  /*
   * DÉDUPLICATION D'AFFICHAGE (§48-§49).
   *
   * Une même Story produit souvent un événement riche (« AFRO PARTY », avec
   * date, line-up et affiche) ET un petit signal dérivé (« DJ invité
   * annoncé »). Les montrer côte à côte fait lire deux fois la même
   * observation, et la version pauvre affaiblit la riche.
   *
   * On masque donc le signal À L'AFFICHAGE quand l'événement le contient
   * déjà. Les DONNÉES restent intactes : le moteur de pression et le
   * Forecast continuent de s'appuyer dessus — c'est une règle de
   * présentation, pas une suppression.
   */
  const signauxVisibles = useMemo(() => {
    const porteurs = new Set(signals.map((s) => s.market_event_id).filter(Boolean))
    void porteurs
    const absorbes = new Set(
      signals.filter((sig) => events.some((e) =>
        e.venue_id !== null && e.venue_id === sig.venue_id
        // Même nuit : deux observations du même soir, au même endroit.
        && e.bucket === sig.bucket
        // …et l'événement dit déjà ce que le signal dit.
        && signalContenuDans(sig, e))).map((s) => s.id))
    return signals.filter((s) => !absorbes.has(s.id))
  }, [signals, events])

  const evenementsVisibles = useMemo(() => {
    const withSignal = new Set(signals.map((s) => s.market_event_id).filter(Boolean))
    return events.filter((e) => !withSignal.has(e.id))
  }, [signals, events])

  /* Combien d'éléments par période — la même donnée alimente les compteurs du
     sélecteur et le choix de la période ouverte par défaut. */
  const comptes = useMemo(() => {
    const c: Partial<Record<Periode, number>> = {}
    for (const x of [...signauxVisibles, ...evenementsVisibles]) {
      c[x.bucket] = (c[x.bucket] ?? 0) + 1
    }
    return c
  }, [signauxVisibles, evenementsVisibles])

  const periodeActive: Periode = periode ?? defaultPeriode(comptes)

  const dansLaPeriode = <T extends { bucket: Periode; past_range: PastRange | null }>(x: T) =>
    x.bucket === periodeActive
    && (periodeActive !== 'PASSE' || x.past_range === passeRange)

  const sigPeriode = useMemo(
    () => trier(
      signauxVisibles.filter(dansLaPeriode).map((s) => ({
        ...s, at: s.event_start_at, distanceKm: distancePour(s.venue_id, data?.venues ?? null),
      })), tri, periodeActive === 'PASSE'),
    [signauxVisibles, periodeActive, passeRange, tri, data])

  const evPeriode = useMemo(
    () => trier(
      evenementsVisibles.filter(dansLaPeriode).map((e) => ({
        ...e, at: e.starts_at as string | null, distanceKm: e.distance_km,
        impact: NIVEAU_IMPACT[e.relevance_level] ?? 'LOW',
        confidence: NIVEAU_CONFIANCE[e.confidence] ?? 'MEDIUM',
      })), tri, periodeActive === 'PASSE'),
    [evenementsVisibles, periodeActive, passeRange, tri])

  const rienDansLaPeriode = sigPeriode.length === 0 && evPeriode.length === 0

  const aucuneVenue = data !== null && data.venues.filter((v) => v.enabled).length === 0

  const ajouterSignal = async () => {
    if (!add.text.trim()) { toast('error', 'Décrivez le signal (texte requis).'); return }
    const body: Record<string, unknown> = { type: add.type, text: add.text.trim() }
    if (add.venue_id) body.venue_id = add.venue_id
    if (add.event_day) body.event_day = add.event_day
    if (add.source_label.trim()) body.source_label = add.source_label.trim()
    const r = await adminMutate<{ ok?: boolean; error?: string }>('POST', '/api/v1/soirees/market/signals', body)
    if (r.status === 200) {
      toast('ok', 'Signal ajouté à la veille.')
      setAddOpen(false)
      setAdd({ venue_id: '', type: 'FREE_ENTRY', text: '', event_day: '', source_label: '' })
      charger()
    } else toast('error', 'Ajout impossible.')
  }

  return (
    <Shell active="soirees" meta={meta}>
      <main className="lc-page lc-stagger hub--soirees so-page">
        <SmartBack fallback="/soirees" />
        <div className="so-section-head">
          <div>
            <h1 className="lc-title"><MapPin size={26} aria-hidden="true" /> Autour de Rennes</h1>
            <p className="lc-subtitle">
              Ce qui pourrait influencer nos soirées — signaux publics vérifiés à leur source, jamais des certitudes.
            </p>
          </div>
          <div className="so-toolbar so-toolbar--inline">
            <Select compact value={typeGroup} options={SIGNAL_TYPE_CHOICES} onChange={setTypeGroup} ariaLabel="Type de signal" />
            <Select compact value={impact} options={IMPACT_OPTIONS} onChange={setImpact} ariaLabel="Impact minimal" />
            <Select compact value={venueFilter} options={venueOptions} onChange={setVenueFilter} ariaLabel="Lieu" />
            <Select compact value={statusFilter} options={STATUS_OPTIONS} onChange={setStatusFilter} ariaLabel="Statut" />
            {/* Discret, jamais permanent à l'écran : c'est une vue de
                nettoyage, pas le mode de travail habituel (§30). */}
            <button type="button" className={`lc-btn${voirMasques ? ' lc-btn--primary' : ''}`}
              aria-pressed={voirMasques} onClick={() => setVoirMasques((o) => !o)}>
              {voirMasques ? <EyeOff size={14} aria-hidden="true" /> : <Eye size={14} aria-hidden="true" />}
              {voirMasques ? 'Vue normale' : 'Éléments masqués'}
            </button>
            {peutGerer ? (
              <button type="button" className="lc-btn" onClick={() => setAddOpen(true)}>
                <Plus size={14} aria-hidden="true" /> Ajouter un signal
              </button>
            ) : null}
          </div>
        </div>

        {!data ? <p className="lc-hint">Chargement…</p>
          : aucuneVenue ? (
            <section className="lc-panel gd-panel"><div className="lc-panel__body so-empty">
              <MapPin size={22} aria-hidden="true" />
              <div>
                <p className="so-empty__title">Commencer la veille concurrentielle</p>
                <p className="lc-hint">
                  Aucun établissement n'est surveillé. Ajoutez les lieux qui comptent
                  (clubs, salles, BDE) — Gestion générera automatiquement les recherches.
                </p>
                {peutGerer ? (
                  <button type="button" className="lc-btn lc-btn--primary" style={{ marginTop: 10 }}
                    onClick={() => navigate('/settings/soirees/veille')}>Ajouter des établissements</button>
                ) : null}
              </div>
            </div></section>
          ) : (
            <>
              {/* ═══ NAVIGATION TEMPORELLE (§13) ═══
                  Une période à la fois, avec son compte. Les périodes vides
                  restent visibles mais désactivées : savoir qu'il n'y a rien
                  la semaine prochaine EST une information. */}
              <div className="mkt-nav">
                <div className="lc-seg mkt-nav__periodes" role="tablist" aria-label="Période">
                  {PERIODES.map((p) => {
                    const n = comptes[p.value] ?? 0
                    return (
                      <button key={p.value} role="tab" type="button"
                        aria-selected={periodeActive === p.value}
                        disabled={n === 0 && periodeActive !== p.value}
                        className={`lc-seg__btn ${periodeActive === p.value ? 'lc-seg__btn--on' : ''}`}
                        onClick={() => setPeriode(p.value)}>
                        {p.label}
                        {n > 0 ? <span className="mkt-nav__n">{n}</span> : null}
                      </button>
                    )
                  })}
                </div>
                <Select compact value={tri} ariaLabel="Trier par"
                  options={TRIS.map((t) => ({ value: t.value, label: t.label }))}
                  onChange={(v) => setTri(v as Tri)} />
              </div>

              {periodeActive === 'PASSE' ? (
                <div className="lc-seg mkt-nav__passe" role="tablist" aria-label="Profondeur">
                  {PAST_RANGES.map((r) => (
                    <button key={r.value} role="tab" type="button"
                      aria-selected={passeRange === r.value}
                      className={`lc-seg__btn ${passeRange === r.value ? 'lc-seg__btn--on' : ''}`}
                      onClick={() => setPasseRange(r.value)}>{r.label}</button>
                  ))}
                </div>
              ) : null}

              {rienDansLaPeriode ? (
                <p className="gd-calm">
                  Rien dans cette période avec ces filtres. La veille tourne en tâche
                  de fond — un silence est aussi une information, tant que les sources
                  sont saines (voir Réglages → Soirées).
                </p>
              ) : (
                /* La clé porte la période : le contenu se REND à nouveau, donc
                   les entrées du Motion System se rejouent, sans page blanche
                   ni indicateur de chargement — les données sont déjà là. */
                <section className="so-mkt-night" key={`${periodeActive}-${passeRange}-${tri}`}>
                  {sigPeriode.length > 0 ? (
                    <div className="so-sigs">
                      {sigPeriode.map((s) => (
                        <SignalCard key={s.id} s={s} onOpen={() => setSelSignal(s)}
                          onMasquer={peutGerer ? () => void masquerSignal(s.id, !s.hidden) : undefined}
                          onIgnorer={peutGerer && s.venue_id ? () => void ignorerType(s) : undefined} />
                      ))}
                    </div>
                  ) : null}
                  {evPeriode.length > 0 ? (
                    <div className="mev-grid">
                      {evPeriode.map((e) => (
                        <MarketEventCard key={e.id} e={e} onOpen={() => setSelEvent(e)}
                          onCapture={setCapture}
                          actions={peutGerer ? (
                            <button type="button" onClick={() => void masquerEvent(e.id, !e.hidden)}>
                              {e.hidden ? 'Réafficher cette soirée' : 'Masquer cette soirée'}
                            </button>
                          ) : undefined} />
                      ))}
                    </div>
                  ) : null}
                </section>
              )}
            </>
          )}

        <p className="lc-hint so-source">
          {(data?.venues ?? []).filter((v) => v.enabled).length} établissement(s) surveillé(s)
          — sources et recherches gérées dans <Link href="/settings/soirees/veille">Paramètres → Veille concurrentielle</Link>.
        </p>

        {/* §20-§24 — le détail vit DANS Gestion. Instagram devient une
            action secondaire du panneau, plus la destination du clic. */}
        <MarketEventDrawer event={selEvent} onClose={() => setSelEvent(null)} />

        {capture ? (
          <div className="so-capture" role="dialog" aria-label="Affiche analysée"
            onClick={() => setCapture(null)}>
            <img src={`/api/v1/market/social/stories/${capture}/capture`}
              alt="Affiche de la Story analysée" />
          </div>
        ) : null}

        {/* ═══ détail d'un signal (§32) ═══ */}
        <Drawer open={selSignal !== null} title={selSignal?.type_label ?? ''} onClose={() => setSelSignal(null)}>
          {selSignal ? (
            <>
              <p className="so-sig__summary" style={{ marginTop: 0, fontSize: 16 }}>{selSignal.summary}</p>
              <dl className="lc-kv">
                <div><dt>Établissement</dt><dd>{selSignal.venue_name ?? '—'}</dd></div>
                {selSignal.market_event_title ? <div><dt>Événement concurrent</dt><dd>{selSignal.market_event_title}</dd></div> : null}
                <div><dt>Date concernée</dt><dd>{selSignal.event_start_at ? jourFr(selSignal.event_start_at) : 'À confirmer'}</dd></div>
                {selSignal.audience ? <div><dt>Public visé</dt><dd>{selSignal.audience === 'female' ? 'Femmes' : selSignal.audience}</dd></div> : null}
                {selSignal.condition_time ? <div><dt>Condition</dt><dd>{selSignal.condition_time}</dd></div> : null}
                <div><dt>Impact estimé</dt><dd>{IMPACT_FR[selSignal.impact]}</dd></div>
                <div><dt>Confiance</dt><dd>{CONF_FR[selSignal.confidence]} ({selSignal.sources.length} source{selSignal.sources.length > 1 ? 's' : ''})</dd></div>
                <div><dt>Statut</dt><dd>{STATUS_FR[selSignal.status] ?? selSignal.status}</dd></div>
                <div><dt>Détecté</dt><dd>{jourFr(selSignal.first_seen_at)} à {heureFr(selSignal.first_seen_at)}</dd></div>
                <div><dt>Dernière vérification</dt><dd>il y a {ageFr(selSignal.last_seen_at)}</dd></div>
              </dl>
              <div className="so-factors">
                <h3 className="so-factors__title">Pourquoi c'est important ?</h3>
                <p className="lc-hint" style={{ margin: 0 }}>
                  {selSignal.impact === 'HIGH'
                    ? 'Une offre commerciale directe (gratuité, avantage fort) chez un concurrent le même soir peut détourner une partie de notre public — le forecast en tient compte comme facteur, sans inventer une perte chiffrée.'
                    : selSignal.impact === 'MEDIUM'
                      ? 'Un événement attractif chez un concurrent le même soir pèse sur le contexte de la nuit — facteur d’ambiance dans la prévision, pas une certitude.'
                      : 'Signal informatif — faible influence attendue sur notre fréquentation.'}
                  {selSignal.confidence === 'LOW' ? ' Confiance faible : à confirmer avant d’en tirer une décision.' : ''}
                </p>
              </div>
              {/* ═══ PREUVES (§24-§29) ═══
                  Un DOMAINE n'est pas une preuve. Ce bloc répond à trois
                  questions dans cet ordre : quelle page exactement, ce qu'on
                  y a lu, et par quel chemin on l'a trouvée. Ce qui n'est pas
                  connu n'est pas affiché — jamais d'ancre devinée. */}
              <div className="so-factors">
                <h3 className="so-factors__title">
                  Preuves
                  <span className="mkt-ev__n">{selSignal.sources.length}</span>
                </h3>
                <ul className="mkt-ev">
                  {selSignal.sources.map((src, i) => {
                    const ev = selSignal.evidence?.[i]
                    return (
                      <li key={i} className="mkt-ev__item">
                        <div className="mkt-ev__title">
                          {ev?.page_title ?? src.name}
                        </div>
                        {src.url ? (
                          <a href={src.url} target="_blank" rel="noopener noreferrer" className="mkt-ev__url">
                            <span className="mkt-ev__domain">{ev?.domain ?? src.name}</span>
                            {ev?.path ? <span className="mkt-ev__path">{ev.path}</span> : null}
                            <ExternalLink size={11} aria-hidden="true" />
                          </a>
                        ) : <span className="lc-hint">Aucune adresse — {src.name}</span>}
                        {ev?.heading ? (
                          <p className="mkt-ev__heading">Trouvé dans « {ev.heading} »</p>
                        ) : null}
                        {ev?.snippet ? (
                          <blockquote className="mkt-ev__snippet">{ev.snippet}</blockquote>
                        ) : null}
                        <dl className="mkt-ev__meta">
                          <dt>Méthode</dt><dd>{sourceKindFr(src)}</dd>
                          {ev?.query ? (<><dt>Requête</dt><dd>{ev.query}</dd></>) : null}
                          {ev?.engine ? (<><dt>Moteur</dt><dd>{ev.engine}</dd></>) : null}
                          <dt>Découverte</dt>
                          <dd>{jourFr(ev?.first_seen_at ?? src.seen_at)}</dd>
                          {ev?.last_seen_at ? (<><dt>Revue</dt><dd>{jourFr(ev.last_seen_at)}</dd></>) : null}
                          {ev?.times_seen && ev.times_seen > 1
                            ? (<><dt>Retrouvée</dt><dd>{ev.times_seen} fois</dd></>) : null}
                          {ev?.published_at ?? src.provenance?.published_at
                            ? (<><dt>Publiée</dt><dd>{jourFr((ev?.published_at ?? src.provenance!.published_at)!)}</dd></>)
                            : null}
                          {src.provenance?.author_handle
                            ? (<><dt>Compte</dt><dd>@{src.provenance.author_handle}</dd></>) : null}
                        </dl>
                        {ev?.fetch_error ? (
                          <p className="mkt-ev__err">Page illisible au dernier passage : {ev.fetch_error}</p>
                        ) : null}
                      </li>
                    )
                  })}
                </ul>
              </div>
              {selSignal.history.length > 0 ? (
                <div className="so-factors">
                  <h3 className="so-factors__title">Versions précédentes</h3>
                  <ul>{selSignal.history.map((h, i) => (
                    <li key={i}>{jourFr(h.at)} : {h.summary}{h.condition_time ? ` (${h.condition_time})` : ''}</li>
                  ))}</ul>
                </div>
              ) : null}
            </>
          ) : null}
        </Drawer>

        {/* L'ancien drawer générique écoutait le MÊME état `selEvent` que le
            panneau canonique : les deux s'ouvraient ensemble. Ce n'était pas
            une propagation de clic, mais deux surfaces abonnées au même état.
            Le détail d'un événement vit désormais dans MarketEventDrawer,
            et nulle part ailleurs (§18-§19). */}

        {/* ═══ ajout manuel (fallback §23 — jamais le flux principal) ═══ */}
        <Drawer open={addOpen} title="Ajouter un signal de veille" onClose={() => setAddOpen(false)}>
          <p className="lc-hint" style={{ marginTop: 0 }}>
            Fallback manuel — pour une info repérée là où la collecte automatique
            ne passe pas (story Instagram, affiche, bouche-à-oreille vérifié).
          </p>
          <Field label="Établissement">
            <Select value={add.venue_id} ariaLabel="Établissement concerné"
              options={[{ value: '', label: '— aucun / autre —' },
                ...(data?.venues ?? []).map((v) => ({ value: v.id, label: v.name }))]}
              onChange={(v) => setAdd({ ...add, venue_id: v })} />
          </Field>
          <Field label="Type de signal">
            <Select value={add.type} options={MANUAL_TYPES} ariaLabel="Type de signal"
              onChange={(v) => setAdd({ ...add, type: v })} />
          </Field>
          <Field label="Texte" helper="Ex. « Entrée gratuite filles avant 01:00 » — heure et date sont extraites automatiquement.">
            <TextInput value={add.text} onChange={(v) => setAdd({ ...add, text: v })}
              placeholder="Décrire le signal" maxLength={400} ariaLabel="Texte du signal" />
          </Field>
          <Field label="Date concernée (optionnel)">
            <input type="date" className="lc-input so-date" value={add.event_day}
              onChange={(e) => setAdd({ ...add, event_day: e.target.value })} aria-label="Date concernée" />
          </Field>
          <Field label="Source" helper="D'où vient l'info — ex. « Story Instagram »." >
            <TextInput value={add.source_label} onChange={(v) => setAdd({ ...add, source_label: v })}
              placeholder="Story Instagram, affiche…" maxLength={120} ariaLabel="Source de l'information" />
          </Field>
          <button type="button" className="lc-btn lc-btn--primary" onClick={() => void ajouterSignal()}>
            Ajouter à la veille
          </button>
        </Drawer>
      </main>
    </Shell>
  )
}
